import { useCallback, useEffect, useRef } from 'react'
import Api from 'Vendor/Api'

/**
 * Keeps one editor on a share, and tells that editor when the share has moved on, in one request a tick.
 *
 * The lease belongs to the server and lapses on its own, because a browser that crashed, slept or was
 * killed sends no release and never will. So the only job here is to keep asking while someone is working
 * and to stop asking once they are not: stopping is how the share gets handed on.
 *
 * Asking continues while the tab is hidden. Hiding a tab is not abandoning it, and a hidden page's timers
 * are throttled to roughly one a minute anyway, which the lease is sized to outlast.
 */

/** Milliseconds between ticks — 15 seconds. */
export const SHARE_TICK_INTERVAL = 15000

/** Milliseconds a share may be left alone before it is handed to whoever wants it next — 10 minutes. */
export const SHARE_IDLE_TIMEOUT = 600000

/**
 * Milliseconds the server's lease survives without a tick — 90 seconds.
 *
 * Mirrors BimboShareLockService::TTL in bimbo-back, which counts in **seconds** because that is what
 * Redis takes: the same duration is 90 there and 90000 here. Change one and the other has to follow.
 */
export const SHARE_LEASE_TTL = 90000

/**
 * Milliseconds before a refusal is questioned once — 2 seconds.
 *
 * A window hands its lease back as it closes, and the editor opening in its place often asks first, so
 * the first answer it gets is a refusal that is already on its way to being untrue. Without this the
 * share reads as somebody else's until the tick after, a quarter of a minute later.
 */
export const SHARE_CLAIM_RETRY = 2000

/**
 * Who holds the lease, from this window's point of view. A second window of the same person is turned away
 * as a colleague would be, since both would replace the whole selection on save.
 */
export type ShareLock = 'mine' | 'taken'

/**
 * A lease belongs to one window, so each one needs a name of its own. Reached through globalThis because
 * `crypto` is an undeclared identifier wherever it is absent, and optional chaining does not save you from
 * that — it throws rather than falling through.
 */
const newWindowId = (): string => (
    'function' === typeof globalThis.crypto?.randomUUID
        ? globalThis.crypto.randomUUID()
        : `w-${Math.random().toString(36).slice(2)}`
)

/**
 * The editor owns its window id rather than this hook, because every request that reports who holds the
 * lease has to carry it — a reload that forgot to would be told its own lease belonged to a stranger.
 */
export const useWindowId = (): string => {
    const id = useRef<string>('')

    if (!id.current) {
        id.current = newWindowId()
    }

    return id.current
}

const editingUrl = (path: string, window: string): string => `${path}/editing?window=${window}`

/**
 * Claims still on the wire, by the lease each would take.
 *
 * An editor closed again within a second sends its release while its own opening claim is still in flight.
 * The claim would land second and take a lease with no window left to hand it back, so the share would sit
 * unavailable for a full lease. A release waits for the claim it might otherwise overtake.
 */
const inFlightClaims = new Map<string, Promise<unknown>>()

/**
 * Takes the lease before the editor asks for anything else.
 *
 * Serialising a large share takes seconds, and a lease claimed only once that lands leaves the share
 * reading as free for every one of them — long enough for a second editor to walk in and take it.
 * Answers `mine` when the request fails, matching what the server does when it cannot reach the store.
 */
export const claimShareLease = (path: string, window: string): Promise<ShareLock> => {
    const url = editingUrl(path, window)

    const claim: Promise<ShareLock> = Api.post(url)
        .then(({data}: {data: {lock?: ShareLock}}) => data?.lock ?? 'mine')
        .catch(() => 'mine' as ShareLock)

    inFlightClaims.set(url, claim)

    return claim.finally(() => {
        if (claim === inFlightClaims.get(url)) {
            inFlightClaims.delete(url)
        }
    })
}

/** Reading a long share is working, so movement and scrolling count as much as a click. */
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel']

export interface ShareEditingTickOptions {
    /** The share's path, or nothing at all when there is no saved share to hold. */
    path?: string
    /** Which window is asking. The lease belongs to this, not to the user. */
    window: string
    /** The fingerprint the window was built on. */
    token?: string
    /** A save is in flight or a dialog is up: keep the lease, report nothing. */
    busy: boolean
    /** A fingerprint the user has already been told about and dismissed. */
    dismissed?: string
    /**
     * `afterIdle` marks the first answer since the ticking stopped, which is the difference between a
     * share that changed under someone working and one that changed while they were away.
     */
    onChanged: (stateToken: string, afterIdle: boolean) => void
    onDeleted: () => void
    onLock: (lock: ShareLock) => void
    interval?: number
    idleTimeout?: number
}

/**
 * Releases waiting to be sent, by the lease each would drop.
 *
 * Dropping a lease compares the holder first, which keeps one window from evicting another. It cannot tell
 * a window apart from itself a moment later, though, and a remount keeps its window id — so a release sent
 * as the old run tears down can overtake the new run's hold and leave the editor holding nothing. Waiting a
 * turn lets the new run take the lease back and call this off.
 */
const pendingReleases = new Map<string, number>()

const useShareEditingTick = (options: ShareEditingTickOptions): void => {
    const {path, window: windowId, interval = SHARE_TICK_INTERVAL} = options
    const latest = useRef<ShareEditingTickOptions>(options)
    const lastInteraction = useRef<number>(Date.now())
    const paused = useRef<boolean>(false)

    useEffect(() => {
        latest.current = options
    })

    const noteInteraction = useCallback(() => {
        lastInteraction.current = Date.now()
    }, [])

    useEffect(() => {
        // Every nested dialog of the form renders in its own portal, so a listener on the form's own
        // tree would miss everything the user does inside one.
        ACTIVITY_EVENTS.forEach(name => document.addEventListener(name, noteInteraction, {passive: true}))

        return () => {
            ACTIVITY_EVENTS.forEach(name => document.removeEventListener(name, noteInteraction))
        }
    }, [noteInteraction])

    useEffect(() => {
        if (!path) {
            return
        }

        let cancelled = false
        let retry: number | undefined

        const url = editingUrl(path, windowId)
        const scheduledRelease = pendingReleases.get(url)

        if (undefined !== scheduledRelease) {
            window.clearTimeout(scheduledRelease)
            pendingReleases.delete(url)
        }

        const tick = () => {
            const {token, idleTimeout = SHARE_IDLE_TIMEOUT} = latest.current

            // Stopping a lease early is what makes the share come free exactly on the timeout rather than
            // a TTL after it.
            if (Date.now() - lastInteraction.current > idleTimeout - SHARE_LEASE_TTL) {
                paused.current = true

                return
            }

            const afterIdle = paused.current
            paused.current = false

            Api.post(url)
                .then(({data}: {data: {stateToken?: string, lock?: ShareLock}}) => {
                    if (cancelled) {
                        return
                    }

                    if (data?.lock) {
                        latest.current.onLock(data.lock)

                        if ('mine' !== data.lock && undefined === retry) {
                            retry = window.setTimeout(tick, SHARE_CLAIM_RETRY)
                        }
                    }

                    const stateToken = data?.stateToken

                    // The window moved on while this was in flight — most often because the user saved,
                    // and answering now would report their own change back to them.
                    if (!stateToken || !token || latest.current.busy || token !== latest.current.token) {
                        return
                    }

                    if (stateToken !== token && stateToken !== latest.current.dismissed) {
                        latest.current.onChanged(stateToken, afterIdle)
                    }
                })
                .catch(error => {
                    // Nothing brings a deleted share back, so stop asking for it.
                    if (!cancelled && 404 === error?.response?.status) {
                        latest.current.onDeleted()
                    }
                })
        }

        // At once rather than one interval in, so a second editor opening the same share seconds later is
        // turned away instead of racing a quarter of a minute.
        tick()

        const timer = window.setInterval(tick, interval)

        const handleVisibilityChange = () => {
            if ('visible' === document.visibilityState) {
                noteInteraction()
                tick()
            }
        }

        /**
         * Not the shared Api client: an ordinary request is abandoned when the page goes away, and
         * `keepalive` is the only thing that survives it. Best effort either way — the lease lapses on its
         * own when this never arrives.
         */
        const release = () => {
            if ('function' !== typeof fetch) {
                return
            }

            fetch(`${Api.defaults.baseURL ?? ''}${url}`, {
                credentials: 'include',
                keepalive: true,
                method: 'DELETE',
            }).catch(() => undefined)
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('pagehide', release)

        return () => {
            cancelled = true
            window.clearInterval(timer)

            if (undefined !== retry) {
                window.clearTimeout(retry)
            }

            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('pagehide', release)

            pendingReleases.set(url, window.setTimeout(() => {
                pendingReleases.delete(url)

                const claim = inFlightClaims.get(url)

                if (undefined === claim) {
                    release()

                    return
                }

                void claim.then(release, release)
            }, 0))
        }
    }, [path, windowId, interval, noteInteraction])
}

export default useShareEditingTick
